import React, { PropsWithChildren } from 'react';
import { Command, useCommandState } from 'cmdk';
import { useCommandPaletteContext } from '../CommandPaletteContext';
import { ScoringDebugPanel } from './ScoringDebugPanel';
import { Chip } from './KeywordChip';
import { CommandAction, CommandActions } from './CommandShortcuts';
import type { Command as CommandType } from '../types';
import styles from '../CommandPalette.module.scss';
import LoadingSpinner from "@imminently/decisively-ui/LoadingSpinner";


// ── Footer.DebugPanel ────────────────────────────────────────────────────────
// The full scoring debug panel (toggle + expanded controls) placed above the
// main footer row inside the footer's flex-column layout.
const FooterDebugPanel: React.FC = () => <ScoringDebugPanel />;

// ── Footer.PageChip ──────────────────────────────────────────────────────────
// Shows the current page name as a chip, falling back to 'global' when on the
// main palette view.
const FooterPageChip: React.FC = () => {
  const { currentPage } = useCommandPaletteContext();
  const label = currentPage
    ? currentPage.pageId.replace(/[-_]/g, ' ')
    : 'global';
  return <Chip label={label} variant="context" />;
};

// ── Footer.Loading ───────────────────────────────────────────────────────────
// Renders a cmdk Command.Loading indicator driven by the context `loading`
// flag. Any plugin can call `setLoading(pluginId, true/false)` to toggle it.
const FooterLoading: React.FC = () => {
  const { loading } = useCommandPaletteContext();
  if (!loading) return null;
  return (
    <Command.Loading className={styles.footerLoadingWrapper}>
      <span className={styles.footerLoadingText}>
        <LoadingSpinner size="sm" />
      </span>
    </Command.Loading>
  );
};

// ── MainAction ────────────────────────────────────────────────────────────────
// Reads the currently highlighted cmdk item and shows its primary action label
// beside the Enter key — e.g. "Navigate ↵", "Open ↵", "Run ↵".
const MainAction: React.FC = () => {
  const selectedValue = useCommandState((state) => state.value);
  const { getAllCommands } = useCommandPaletteContext();
  const allCommands = getAllCommands();
  const selected = allCommands.find((cmd: CommandType) => cmd.id === selectedValue);
  const label = selected?.primary?.label ?? 'Run';
  return <CommandAction text={label} shortcutKey="enter" />;
};

// ── Footer.Actions ───────────────────────────────────────────────────────────
// The "Actions" popover (mod+k) listing all registered keyboard shortcuts.
const FooterActions: React.FC = () => <CommandActions />;

// ── Footer (root) ─────────────────────────────────────────────────────────────
// Layout:
//   ┌─ footerRow (flex-row) ───────────────────────────────────────────────────┐
//   │  [ PageChip  Loading ]        [spacer]   [ MainAction | Actions ]        │
//   └──────────────────────────────────────────────────────────────────────────┘
const FooterRoot: React.FC<PropsWithChildren> = ({ children }) => (
  <div className={styles.footer}>
    <div className={styles.footerRow}>
      <div className={styles.footerLeft}>
        <Footer.PageChip />
        <Footer.Loading />
      </div>
      <div className={styles.footerRight}>
        <div className={styles.footerActionsArea}>
          {children}
          <MainAction />
          <span className={styles.footerDivider} aria-hidden="true" />
          <Footer.Actions />
        </div>
      </div>
    </div>
  </div>
);

/**
 * Footer namespace — assembled from sub-components.
 *
 * @example
 * ```tsx
 * <Footer />                  // renders everything
 * <Footer.PageChip />         // current page context chip
 * <Footer.DebugPanel />       // scoring debug toggle + controls
 * <Footer.Loading />          // loading indicator (driven by context)
 * <Footer.Actions />          // "Actions" popover (mod+k)
 * ```
 */
export const Footer = Object.assign(FooterRoot, {
  DebugPanel: FooterDebugPanel,
  PageChip: FooterPageChip,
  Loading: FooterLoading,
  Actions: FooterActions,
});
